import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  $isRootOrShadowRoot,
  $isTextNode,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  KEY_DOWN_COMMAND,
  LexicalEditor,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
} from 'lexical';
import {
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode,
  HeadingTagType,
} from '@lexical/rich-text';
import {
  $isListNode,
  INSERT_CHECK_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode,
  REMOVE_LIST_COMMAND,
} from '@lexical/list';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { $findMatchingParent, $getNearestNodeOfType, mergeRegister } from '@lexical/utils';
import { $createCodeNode } from '@lexical/code';
import { INSERT_TABLE_COMMAND } from '@lexical/table';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $setBlocksType } from '@lexical/selection';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  StrikethroughS,
  Code,
  FormatListBulleted,
  FormatListNumbered,
  Undo,
  Redo,
  Link,
  Subscript,
  Superscript,
  FormatClear,
  TextFormat,
} from '@mui/icons-material';
import classes from './ToolbarPlugin.module.scss';

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);

type BlockType =
  | 'paragraph'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'bullet'
  | 'number'
  | 'check'
  | 'quote'
  | 'code'
  | 'table';

interface BlockItem {
  type: BlockType;
  label: string;
  icon: string;
  shortcut?: string;
}

const BLOCK_ITEMS: BlockItem[] = [
  { type: 'paragraph', label: 'Normal', icon: '¶', shortcut: IS_MAC ? '⌘⌥0' : 'Ctrl+Alt+0' },
  { type: 'h1', label: 'Heading 1', icon: 'H1', shortcut: IS_MAC ? '⌘⌥1' : 'Ctrl+Alt+1' },
  { type: 'h2', label: 'Heading 2', icon: 'H2', shortcut: IS_MAC ? '⌘⌥2' : 'Ctrl+Alt+2' },
  { type: 'h3', label: 'Heading 3', icon: 'H3', shortcut: IS_MAC ? '⌘⌥3' : 'Ctrl+Alt+3' },
  { type: 'h4', label: 'Heading 4', icon: 'H4' },
  { type: 'h5', label: 'Heading 5', icon: 'H5' },
  { type: 'h6', label: 'Heading 6', icon: 'H6' },
  { type: 'bullet', label: 'Bullet List', icon: '•', shortcut: IS_MAC ? '⌘⇧8' : 'Ctrl+Shift+8' },
  { type: 'number', label: 'Numbered List', icon: '1.', shortcut: IS_MAC ? '⌘⇧7' : 'Ctrl+Shift+7' },
  { type: 'check', label: 'Check List', icon: '☑', shortcut: IS_MAC ? '⌘⇧9' : 'Ctrl+Shift+9' },
  { type: 'quote', label: 'Quote', icon: '"' },
  { type: 'code', label: 'Code Block', icon: '</>' },
  { type: 'table', label: 'Table', icon: '▦' },
];

const blockTypeLabels: Record<BlockType, string> = Object.fromEntries(
  BLOCK_ITEMS.map((item) => [item.type, item.label]),
) as Record<BlockType, string>;

/**
 * Positions a portaled dropdown menu under its trigger, clamped to the viewport
 * so it never overflows the right/bottom edge (flips above if it would).
 * Imperative (sets style on the menu element) so it measures the real menu size.
 */
function useClampedMenuPosition(
  isOpen: boolean,
  triggerRef: React.RefObject<HTMLElement>,
  menuRef: React.RefObject<HTMLElement>,
) {
  useEffect(() => {
    if (!isOpen || !triggerRef.current || !menuRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const menu = menuRef.current;
    const menuW = menu.offsetWidth;
    const menuH = menu.offsetHeight;
    const left = Math.max(4, Math.min(rect.left, window.innerWidth - menuW - 4));
    let top = rect.bottom + 2;
    if (top + menuH > window.innerHeight - 4) {
      top = Math.max(4, rect.top - menuH - 2); // flip above the trigger
    }
    menu.style.top = `${top}px`;
    menu.style.left = `${left}px`;
  }, [isOpen, triggerRef, menuRef]);
}

function BlockFormatDropdown({
  blockType,
  editor,
}: {
  blockType: BlockType;
  editor: LexicalEditor;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const formatBlock = (type: BlockType) => {
    if (type === 'bullet') {
      editor.dispatchCommand(
        blockType !== 'bullet' ? INSERT_UNORDERED_LIST_COMMAND : REMOVE_LIST_COMMAND,
        undefined,
      );
    } else if (type === 'number') {
      editor.dispatchCommand(
        blockType !== 'number' ? INSERT_ORDERED_LIST_COMMAND : REMOVE_LIST_COMMAND,
        undefined,
      );
    } else if (type === 'check') {
      editor.dispatchCommand(
        blockType !== 'check' ? INSERT_CHECK_LIST_COMMAND : REMOVE_LIST_COMMAND,
        undefined,
      );
    } else if (type === 'table') {
      // Table is an insert action (not a block-type conversion) — inserts a 3x3
      // table with a header row at the cursor, matching the slash/"+" picker.
      editor.dispatchCommand(INSERT_TABLE_COMMAND, {
        columns: '3',
        rows: '3',
        includeHeaders: { rows: true, columns: false },
      });
    } else {
      editor.update(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;
        switch (type) {
          case 'paragraph':
            $setBlocksType(selection, () => $createParagraphNode());
            break;
          case 'h1':
          case 'h2':
          case 'h3':
          case 'h4':
          case 'h5':
          case 'h6':
            $setBlocksType(selection, () => $createHeadingNode(type as HeadingTagType));
            break;
          case 'quote':
            $setBlocksType(selection, () => $createQuoteNode());
            break;
          case 'code':
            $setBlocksType(selection, () => $createCodeNode());
            break;
        }
      });
    }
    setIsOpen(false);
  };

  const currentItem = BLOCK_ITEMS.find((item) => item.type === blockType);
  useClampedMenuPosition(isOpen, triggerRef, menuRef);

  const menuContent = isOpen ? (
    <div ref={menuRef} className={`${classes.dropdownMenu} ${classes.dropdownMenuFixed}`}>
      {BLOCK_ITEMS.map((item) => (
        <button
          key={item.type}
          className={`${classes.dropdownItem} ${blockType === item.type ? classes.dropdownItemActive : ''}`}
          onClick={() => formatBlock(item.type)}
        >
          <span className={classes.dropdownIcon}>{item.icon}</span>
          <span className={classes.dropdownLabel}>{item.label}</span>
          {item.shortcut && <span className={classes.dropdownShortcut}>{item.shortcut}</span>}
          {blockType === item.type && <span className={classes.dropdownCheck}>✓</span>}
        </button>
      ))}
    </div>
  ) : null;

  return (
    <div className={classes.blockTypeDropdown} ref={dropdownRef}>
      <button
        ref={triggerRef}
        className={`${classes.toolbarBtn} ${classes.blockTypeBtn}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Block Type"
      >
        <span className={classes.blockTypeIcon}>{currentItem?.icon ?? '¶'}</span>
        <span className={classes.blockTypeLabel}>{currentItem?.label ?? 'Normal'}</span>
        <span className={classes.dropdownArrow}>▾</span>
      </button>
      {menuContent && createPortal(menuContent, document.body)}
    </div>
  );
}

/** Apply a text-content transformation to all selected text nodes */
function applyTextTransform(editor: LexicalEditor, transform: (text: string) => string) {
  editor.update(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      selection.getNodes().forEach((node) => {
        if ($isTextNode(node)) node.setTextContent(transform(node.getTextContent()));
      });
    }
  });
}

function AaDropdown({
  editor,
  isSubscript,
  isSuperscript,
}: {
  editor: LexicalEditor;
  isSubscript: boolean;
  isSuperscript: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const clearFormatting = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        selection.getNodes().forEach((node) => {
          if ($isTextNode(node)) node.setFormat(0);
        });
      }
    });
    setIsOpen(false);
  };

  const item = (active: boolean, onClick: () => void, icon: React.ReactNode, label: string) => (
    <button
      key={label}
      className={`${classes.dropdownItem} ${active ? classes.dropdownItemActive : ''}`}
      onClick={onClick}
    >
      <span className={classes.dropdownIcon}>{icon}</span>
      <span className={classes.dropdownLabel}>{label}</span>
      {active && <span className={classes.dropdownCheck}>✓</span>}
    </button>
  );

  useClampedMenuPosition(isOpen, triggerRef, menuRef);

  const menuContent = isOpen ? (
    <div ref={menuRef} className={`${classes.dropdownMenu} ${classes.dropdownMenuFixed}`}>
      {item(
        false,
        () => {
          applyTextTransform(editor, (s) => s.toUpperCase());
          setIsOpen(false);
        },
        <span className={classes.caseIconSmall}>ABC</span>,
        'Uppercase',
      )}
      {item(
        false,
        () => {
          applyTextTransform(editor, (s) => s.toLowerCase());
          setIsOpen(false);
        },
        <span className={classes.caseIconSmall}>abc</span>,
        'Lowercase',
      )}
      {item(
        false,
        () => {
          applyTextTransform(editor, (s) => s.replace(/\b\w/g, (c) => c.toUpperCase()));
          setIsOpen(false);
        },
        <span className={classes.caseIconSmall}>Tt</span>,
        'Capitalize',
      )}
      <div className={classes.dropdownDivider} />
      {item(
        isSubscript,
        () => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'subscript');
          setIsOpen(false);
        },
        <Subscript fontSize="small" />,
        'Subscript',
      )}
      {item(
        isSuperscript,
        () => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'superscript');
          setIsOpen(false);
        },
        <Superscript fontSize="small" />,
        'Superscript',
      )}
      <div className={classes.dropdownDivider} />
      {item(false, clearFormatting, <FormatClear fontSize="small" />, 'Clear Formatting')}
    </div>
  ) : null;

  return (
    <div className={classes.aaDropdown} ref={dropdownRef}>
      <button
        ref={triggerRef}
        className={`${classes.toolbarBtn} ${isSubscript || isSuperscript ? classes.active : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Text Formatting"
      >
        <TextFormat fontSize="small" />
      </button>
      {menuContent && createPortal(menuContent, document.body)}
    </div>
  );
}

function ToolbarDivider() {
  return <span className={classes.toolbarDivider} />;
}

export function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext();
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [blockType, setBlockType] = useState<BlockType>('paragraph');
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isCode, setIsCode] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [isSubscript, setIsSubscript] = useState(false);
  const [isSuperscript, setIsSuperscript] = useState(false);

  const updateToolbar = useCallback(() => {
    try {
      editor.read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;
        setIsBold(selection.hasFormat('bold'));
        setIsItalic(selection.hasFormat('italic'));
        setIsUnderline(selection.hasFormat('underline'));
        setIsStrikethrough(selection.hasFormat('strikethrough'));
        setIsCode(selection.hasFormat('code'));
        setIsSubscript(selection.hasFormat('subscript'));
        setIsSuperscript(selection.hasFormat('superscript'));

        const node = selection.anchor.getNode();
        const parent = node.getParent();
        setIsLink($isLinkNode(parent) || $isLinkNode(node));

        const anchorNode = selection.anchor.getNode();
        let element =
          anchorNode.getKey() === 'root'
            ? anchorNode
            : $findMatchingParent(anchorNode, (e) => {
                const p = e.getParent();
                return p !== null && $isRootOrShadowRoot(p);
              });

        if (element === null) {
          element = anchorNode.getTopLevelElementOrThrow();
        }

        const elementKey = element.getKey();
        const elementDOM = editor.getElementByKey(elementKey);

        if (elementDOM !== null) {
          if ($isListNode(element)) {
            const parentList = $getNearestNodeOfType<ListNode>(anchorNode, ListNode);
            const type = parentList ? parentList.getListType() : element.getListType();
            if (type === 'bullet') setBlockType('bullet');
            else if (type === 'number') setBlockType('number');
            else if (type === 'check') setBlockType('check');
          } else {
            const type = $isHeadingNode(element) ? element.getTag() : element.getType();
            if (type in blockTypeLabels) {
              setBlockType(type as BlockType);
            } else {
              setBlockType('paragraph');
            }
          }
        }
      });
    } catch {
      // Editor may not be active
    }
  }, [editor]);

  // Register keyboard shortcuts for heading/list/quote/link formatting
  useEffect(() => {
    return editor.registerCommand(
      KEY_DOWN_COMMAND,
      (event: KeyboardEvent) => {
        const mod = IS_MAC ? event.metaKey : event.ctrlKey;
        const modAlt = mod && event.altKey;
        const modShift = mod && event.shiftKey;

        // Heading shortcuts: Cmd/Ctrl + Alt + 0-3
        if (modAlt && event.key === '0') {
          event.preventDefault();
          editor.update(() => {
            const sel = $getSelection();
            if ($isRangeSelection(sel)) $setBlocksType(sel, () => $createParagraphNode());
          });
          return true;
        }
        if (modAlt && event.key === '1') {
          event.preventDefault();
          editor.update(() => {
            const sel = $getSelection();
            if ($isRangeSelection(sel)) $setBlocksType(sel, () => $createHeadingNode('h1'));
          });
          return true;
        }
        if (modAlt && event.key === '2') {
          event.preventDefault();
          editor.update(() => {
            const sel = $getSelection();
            if ($isRangeSelection(sel)) $setBlocksType(sel, () => $createHeadingNode('h2'));
          });
          return true;
        }
        if (modAlt && event.key === '3') {
          event.preventDefault();
          editor.update(() => {
            const sel = $getSelection();
            if ($isRangeSelection(sel)) $setBlocksType(sel, () => $createHeadingNode('h3'));
          });
          return true;
        }

        // List shortcuts: Cmd/Ctrl + Shift + 7/8/9
        if (modShift && event.key === '7') {
          event.preventDefault();
          editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
          return true;
        }
        if (modShift && event.key === '8') {
          event.preventDefault();
          editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
          return true;
        }
        if (modShift && event.key === '9') {
          event.preventDefault();
          editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined);
          return true;
        }

        // Link: Ctrl+K (Mac: Cmd+K)
        if (mod && !event.altKey && !event.shiftKey && event.key === 'k') {
          event.preventDefault();
          editor.dispatchCommand(TOGGLE_LINK_COMMAND, 'https://');
          return true;
        }

        return false;
      },
      COMMAND_PRIORITY_LOW,
    );
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(() => {
        updateToolbar();
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateToolbar();
          return false;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        (payload) => {
          setCanUndo(payload);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        (payload) => {
          setCanRedo(payload);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
    );
  }, [editor, updateToolbar]);

  const insertLink = useCallback(() => {
    if (!isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, 'https://');
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [editor, isLink]);

  return (
    <div className={classes.toolbar} data-testid="lexical-toolbar">
      <button
        disabled={!canUndo}
        onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)}
        className={classes.toolbarBtn}
        title={`Undo (${IS_MAC ? '⌘Z' : 'Ctrl+Z'})`}
      >
        <Undo fontSize="small" />
      </button>
      <button
        disabled={!canRedo}
        onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)}
        className={classes.toolbarBtn}
        title={`Redo (${IS_MAC ? '⌘Y' : 'Ctrl+Y'})`}
      >
        <Redo fontSize="small" />
      </button>

      <ToolbarDivider />

      <BlockFormatDropdown blockType={blockType} editor={editor} />

      <ToolbarDivider />

      <button
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
        className={`${classes.toolbarBtn} ${isBold ? classes.active : ''}`}
        title={`Bold (${IS_MAC ? '⌘B' : 'Ctrl+B'})`}
      >
        <FormatBold fontSize="small" />
      </button>
      <button
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
        className={`${classes.toolbarBtn} ${isItalic ? classes.active : ''}`}
        title={`Italic (${IS_MAC ? '⌘I' : 'Ctrl+I'})`}
      >
        <FormatItalic fontSize="small" />
      </button>
      <button
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')}
        className={`${classes.toolbarBtn} ${isUnderline ? classes.active : ''}`}
        title={`Underline (${IS_MAC ? '⌘U' : 'Ctrl+U'})`}
      >
        <FormatUnderlined fontSize="small" />
      </button>
      <button
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')}
        className={`${classes.toolbarBtn} ${isStrikethrough ? classes.active : ''}`}
        title="Strikethrough"
      >
        <StrikethroughS fontSize="small" />
      </button>
      <button
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code')}
        className={`${classes.toolbarBtn} ${isCode ? classes.active : ''}`}
        title="Inline Code"
      >
        <Code fontSize="small" />
      </button>
      <button
        onClick={insertLink}
        className={`${classes.toolbarBtn} ${isLink ? classes.active : ''}`}
        title={`Link (${IS_MAC ? '⌘K' : 'Ctrl+K'})`}
      >
        <Link fontSize="small" />
      </button>
      <AaDropdown editor={editor} isSubscript={isSubscript} isSuperscript={isSuperscript} />

      <ToolbarDivider />

      <button
        onClick={() => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)}
        className={`${classes.toolbarBtn} ${blockType === 'bullet' ? classes.active : ''}`}
        title={`Bullet List (${IS_MAC ? '⌘⇧8' : 'Ctrl+Shift+8'})`}
      >
        <FormatListBulleted fontSize="small" />
      </button>
      <button
        onClick={() => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)}
        className={`${classes.toolbarBtn} ${blockType === 'number' ? classes.active : ''}`}
        title={`Numbered List (${IS_MAC ? '⌘⇧7' : 'Ctrl+Shift+7'})`}
      >
        <FormatListNumbered fontSize="small" />
      </button>
    </div>
  );
}
